import React, { useState } from "react";
import { motion } from "framer-motion";
import { api, GUEST_TOKEN_KEY, formatApiErrorDetail } from "@/lib/api";
import Icon from "@/components/Icon";

const HERO = "/hero.webp";

export default function GuestGate({ onUnlock }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/guest/access", { code });
      localStorage.setItem(GUEST_TOKEN_KEY, data.token);
      onUnlock();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to verify code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background" data-testid="guest-gate">
      {/* Left — image panel */}
      <div className="relative hidden lg:block overflow-hidden">
        <motion.img
          src={HERO}
          alt="Desert modern home"
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className="absolute inset-0 bg-[#2C3325]/35" />
        <div className="absolute bottom-0 left-0 p-12 text-[#F7F6F2]">
          <p className="overline text-[#F7F6F2]/80">La Quinta, California</p>
          <p className="font-display text-4xl leading-none mt-3">Via Solana</p>
        </div>
      </div>

      {/* Right — form panel (hero banner on top for mobile) */}
      <div className="relative flex flex-col">
        <div className="relative lg:hidden h-56 sm:h-72 overflow-hidden shrink-0" data-testid="gate-mobile-hero">
          <motion.img
            src={HERO}
            alt="Desert modern home"
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          />
          <div className="absolute inset-0 bg-[#2C3325]/35" />
          <div className="absolute bottom-0 left-0 p-6 text-[#F7F6F2]">
            <p className="overline text-[#F7F6F2]/80">La Quinta, California</p>
            <p className="font-display text-2xl leading-none mt-2">Via Solana</p>
          </div>
        </div>

        <div className="relative flex-1 flex items-center justify-center px-6 sm:px-16 py-20">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        >
          <div className="inline-flex items-center gap-2 text-accent">
            <Icon name="Key" size={20} weight="duotone" />
            <span className="subhead text-accent text-sm">Private Guidebook</span>
          </div>

          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[0.95] mt-6 text-foreground">
            Welcome
            <br />
            home.
          </h1>
          <p className="text-base leading-relaxed text-muted-foreground mt-5 max-w-sm">
            Enter the access code from your booking confirmation to view your
            personalized guide to Via Solana.
          </p>

          <form onSubmit={submit} className="mt-10">
            <label className="subhead text-accent text-sm">Access Code</label>
            <input
              data-testid="guest-code-input"
              type="text"
              value={code}
              autoFocus
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter your code"
              className="mt-3 w-full bg-transparent border-b-2 border-foreground/25 focus:border-accent outline-none py-3 text-2xl font-display tracking-wide text-foreground placeholder:text-foreground/25 transition-colors"
            />

            {error && (
              <motion.p
                data-testid="guest-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-destructive text-sm mt-4"
              >
                {error}
              </motion.p>
            )}

            <button
              data-testid="guest-unlock-btn"
              type="submit"
              disabled={loading}
              className="group mt-10 inline-flex items-center gap-3 bg-foreground text-primary-foreground pl-8 pr-6 py-4 rounded-full hover:bg-accent transition-colors duration-300 disabled:opacity-60"
            >
              <span className="text-sm font-semibold tracking-wide">
                {loading ? "Unlocking…" : "Enter Guidebook"}
              </span>
              <span className="transition-transform duration-300 group-hover:translate-x-1">
                <Icon name="DoorOpen" size={18} weight="bold" />
              </span>
            </button>
          </form>

          <a
            href="/admin"
            data-testid="admin-link"
            className="mt-16 inline-flex items-center gap-2 border border-border rounded-full px-5 py-2.5 text-sm font-semibold text-muted-foreground hover:text-accent hover:border-accent transition-colors"
          >
            <Icon name="Key" size={15} weight="bold" />
            Owner login
          </a>
        </motion.div>
        </div>
      </div>
    </div>
  );
}
